import seoJson from "@/router/seo.json";
import { useEffect } from "react";
import { ImageNode } from "@noli/core";
import { BoardStore, langStore, useIsReady } from "@noli/business";
import { toast } from "@/components/Toast";
import { action, computed, makeObservable, observable } from "mobx";
import { CacheType } from "@/types/cache";

export const changeURLParam = (url: string, name: string, value: string) => {
  const reg = eval("/([?|&]" + name + "=)[^&]*/gi");
  value = value.toString().replace(/(^\s*)|(\s*$)/g, ""); // 移除首尾空格
  let url2;
  if (!value) {
    // remove
    url2 = url.replace(reg, "");
  } else {
    if (url.match(reg)) {
      // edit
      url2 = url.replace(reg, "$1" + value);
    } else {
      // add
      url2 = url + (url.indexOf("?") > -1 ? "&" : "?") + name + "=" + value;
    }
  }
  return url2;
};
export const changeURLStatic = (name: string, value: string) => {
  const url = changeURLParam(location.href, name, value); // 修改 URL 参数
  history.replaceState(null, null as any, url); // 替换地址栏
};
export function base64ToBlob(urlData: string, type: string) {
  const arr: any = urlData.split(",");
  const mime = arr[0].match(/:(.*?);/)[1] || type;
  // 去掉url的头，并转化为byte
  const bytes = window.atob(arr[1]);
  // 处理异常,将ascii码小于0的转换为大于0
  const ab = new ArrayBuffer(bytes.length);
  // 生成视图（直接针对内存）：8位无符号整数，长度1个字节
  const ia = new Uint8Array(ab);
  for (let i = 0; i < bytes.length; i++) {
    ia[i] = bytes.charCodeAt(i);
  }
  return new Blob([ab], {
    type: mime,
  });
}
const bindHistoryEvent = function (type: string) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const historyEvent = history[type];
  return function () {
    // eslint-disable-next-line prefer-rest-params,@typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line prefer-rest-params
    const newEvent = historyEvent.apply(this, arguments);
    const e = new Event(type);
    // eslint-disable-next-line prefer-rest-params,@typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line prefer-rest-params
    e.arguments = arguments;
    window.dispatchEvent(e);
    return newEvent;
  };
};
//设置SEO相关Meta
const setMeta = () => {
  const lang = langStore.tgt ?? "zh";
  Object.keys(seoJson as any).forEach((key: any) => {
    if (location.pathname.includes(key)) {
      const { title, description } = seoJson[key as keyof typeof seoJson];
      document.title = title[lang];
      const meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      meta.setAttribute("content", description[lang]);
      document.head.appendChild(meta);
    }
  });
};
//新增TDK
export const useTDK = () => {
  const lang = langStore.tgt ?? "zh";

  history.pushState = bindHistoryEvent("pushState");
  history.replaceState = bindHistoryEvent("replaceState");
  return useEffect(() => {
    setMeta();
    //监听url变化
    window.addEventListener("popstate", setMeta, false);
    window.addEventListener("pushState", setMeta, false);
    window.addEventListener("replaceState", setMeta, false);

    return () => {
      window.removeEventListener("popstate", setMeta, false);
      window.removeEventListener("pushState", setMeta, false);
      window.removeEventListener("replaceState", setMeta, false);
    };
  }, [lang]);
};

export const urlQueryParser = (url: string) => {
  const deCoded = decodeURIComponent(url);
  const queryIdx = deCoded.indexOf("?");
  const queries = url.slice(queryIdx + 1);
  const res: { [k: string]: any } = {};
  let key = "",
    val = "",
    kflag = true;
  for (let i = 0; i <= queries.length; i++) {
    if (queries[i] === "&" || i === queries.length) {
      res[key] = val;
      key = "";
      val = "";
      kflag = !kflag;
      continue;
    }
    if (queries[i] === "=" || queries[i] === "&") {
      kflag = !kflag;
      continue;
    }
    if (kflag) {
      key += queries[i];
    } else {
      val += queries[i];
    }
  }
  return res;
  // IE不支持，支付宝环境不要用
  // return Object.fromEntries(
  //   deCoded
  //     .slice(queryIdx + 1)
  //     .split("&")
  //     .map((pair: string) => pair.split("=") as unknown as readonly [string, string]),
  // );
};

export const useChannelTrace = () => {
  const { utm_source } = urlQueryParser(location.href);
  let channelTrace = "";
  if (utm_source) {
    channelTrace = utm_source;
  } else if (document.referrer) {
    const host = new URL(document.referrer).host;
    if (!/(nolibox|localhost)/.test(host)) {
      channelTrace = host;
    }
  }
  if (channelTrace) sessionStorage.setItem("channelTrace", channelTrace);
};

export const copy = (value: string, tips?: string) => {
  const input = document.createElement("input");
  input.value = value;
  document.body.appendChild(input);
  input.select();
  if (document.execCommand("copy")) {
    document.execCommand("copy");
    toast({
      status: "success",
      position: "top",
      description: tips,
    });
  }
  document.body.removeChild(input);
};

export class PageLoadManager<T> {
  pageDataCache: Map<number, T> = new Map();
  pageIdx = 1;
  total = 0;
  isFetching = false;
  constructor(
    public fetchHandler: (pageIdx: number, pageSize: number) => Promise<{ total: number; data: T }>,
    public pageSize = 20,
  ) {
    makeObservable(this, {
      total: observable,
      pageIdx: observable,
      data: computed,
      maxPage: computed,
      hasMore: computed,
      next: action,
      fetch: action,
      refreshAll: action,
      reset: action,
    });
  }

  get data() {
    return Array.from(this.pageDataCache.values()).flat();
  }

  get maxPage() {
    return Math.ceil(this.total / this.pageSize);
  }

  get hasMore() {
    return this.pageIdx < this.maxPage;
  }

  next = async () => {
    if (!this.hasMore) return this.data;
    if (this.isFetching) return this.data;

    this.isFetching = true;
    this.pageIdx++;
    const { data, total } = await this.fetchHandler(this.pageIdx, this.pageSize).finally(() => {
      this.isFetching = false;
    });
    this.total = total;
    this.pageDataCache.set(this.pageIdx, data);
    return this.data;
  };

  fetch = async () => {
    if (this.isFetching) return this.data;
    this.isFetching = true;
    const { data, total } = await this.fetchHandler(this.pageIdx, this.pageSize).finally(() => {
      this.isFetching = false;
    });
    this.total = total;
    this.pageDataCache.set(this.pageIdx, data);
    return this.data;
  };

  refreshAll = async () => {
    if (this.isFetching) return this.data;
    this.isFetching = true;

    const tasks: Promise<{ total: number; data: T }>[] = [];
    this.pageDataCache.forEach((_, page) => {
      tasks.push(this.fetchHandler(page, this.pageSize));
    });
    const res = await Promise.all(tasks).finally(() => {
      this.isFetching = false;
    });
    res.forEach(({ total, data }, idx) => {
      this.total = total;
      this.pageDataCache.set(idx + 1, data);
    });
    return this.data;
  };

  reset = () => {
    this.pageDataCache.clear();
    this.pageIdx = 1;
    this.total = 0;
  };
}

/**
 * 修复canvas画图模糊的问题！
 * @param canvas
 * @param ctx
 */
export function adaptDPR(canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) {
  // 在初始化 canvas 的时候就要调用该方法
  const dpr = window.devicePixelRatio;
  const { width, height } = canvas;
  // 重新设置 canvas 自身宽高大小和 css 大小。放大 canvas；css 保持不变，因为我们需要那么多的点
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  canvas.style.width = width + "px";
  canvas.style.height = height + "px";
  // 直接用 scale 放大整个坐标系，相对来说就是放大了每个绘制操作
  ctx.scale(dpr, dpr);
  // 接下来的绘制操作和往常一样，比如画个矩形 ctx2d.strokeRect(x, y, w, h);原来该怎么算就怎么算，该怎么调用还是怎么调用
}

const defaultPreview =
  "http://nolipix-preview.oss-cn-hangzhou.aliyuncs.com/creator/projectSnapshot/defaultPreview.png";

export async function snapshotBoard(
  localCache: CacheType,
  userId?: number | string,
  useDefault = false,
) {
  const boardReady = useIsReady();
  if (!userId || useDefault) return defaultPreview;
  if (!boardReady) return localCache.preview || defaultPreview;
  const nodes = BoardStore.api.graph.allNodes.filter((node) => node.type === "image");
  const topThree = nodes.slice(Math.max(nodes.length - 3, 0)) as ImageNode[];
  const url = topThree.map((n) => n.renderParams.src).join(",");
  return url || defaultPreview;
}
